"use client";

import { Button } from "@heroui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  maxVisiblePages?: number;
}

export default function Pagination({
  currentPage,
  totalItems,
  itemsPerPage,
  maxVisiblePages = 5,
}: PaginationProps) {
  const searchParams = useSearchParams();
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Don't show pagination if there's only one page
  if (totalPages <= 1) {
    return null;
  }

  // Use fewer visible pages on mobile to prevent overflow
  const mobileMaxPages = 3;
  const desktopMaxPages = maxVisiblePages;

  const createPageURL = (page: number) => {
    // Create a fresh URLSearchParams from current search params
    const params = new URLSearchParams(searchParams.toString());

    // Always remove the existing page parameter first
    params.delete("page");
    // Remove filters=on parameter for cleaner URLs
    params.delete("filters");

    // Only add page parameter if it's not page 1
    if (page > 1) {
      params.set("page", page.toString());
    }

    // Get current pathname to maintain URL structure (including country/city paths)
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "/events";
    const queryString = params.toString();
    const finalURL = queryString
      ? `${currentPath}?${queryString}`
      : currentPath;

    return finalURL;
  };

  // Calculate which page numbers to show - use fewer on mobile
  const getVisiblePages = (maxPages: number) => {
    const pages: number[] = [];
    const half = Math.floor(maxPages / 2);

    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, currentPage + half);

    // Adjust if we're near the beginning or end
    if (end - start + 1 < maxPages) {
      if (start === 1) {
        end = Math.min(totalPages, start + maxPages - 1);
      } else {
        start = Math.max(1, end - maxPages + 1);
      }
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const mobileVisiblePages = getVisiblePages(mobileMaxPages);
  const desktopVisiblePages = getVisiblePages(desktopMaxPages);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col gap-3 items-center justify-center mt-8">
      {/* Results info */}
      <div className="text-xs text-default-500">
        Showing {startItem}-{endItem} of {totalItems} events
      </div>

      {/* Mobile Pagination (hidden on desktop) */}
      <nav
        className="flex md:hidden items-center gap-1"
        aria-label="Pagination Navigation"
      >
        {/* Mobile Previous button */}
        {currentPage > 1 ? (
          <Button
            as={Link}
            href={createPageURL(currentPage - 1)}
            variant="flat"
            size="sm"
            className="min-w-unit-8"
            isIconOnly
            aria-label={`Go to page ${currentPage - 1}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            variant="flat"
            size="sm"
            isDisabled
            className="min-w-unit-8"
            isIconOnly
            aria-label="Previous page (disabled)"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}

        {/* Mobile page numbers */}
        {mobileVisiblePages.map((page) => {
          const isCurrentPage = page === currentPage;
          return (
            <Button
              key={page}
              as={Link}
              href={createPageURL(page)}
              variant={isCurrentPage ? "solid" : "flat"}
              color={isCurrentPage ? "primary" : "default"}
              size="sm"
              className={`min-w-unit-8 ${isCurrentPage ? "text-primary-foreground" : "text-ink"}`}
              aria-label={`Go to page ${page}`}
              aria-current={isCurrentPage ? "page" : undefined}
            >
              {page}
            </Button>
          );
        })}

        {/* Mobile Next button */}
        {currentPage < totalPages ? (
          <Button
            as={Link}
            href={createPageURL(currentPage + 1)}
            variant="flat"
            size="sm"
            className="min-w-unit-8"
            isIconOnly
            aria-label={`Go to page ${currentPage + 1}`}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            variant="flat"
            size="sm"
            isDisabled
            className="min-w-unit-8"
            isIconOnly
            aria-label="Next page (disabled)"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </nav>

      {/* Desktop Pagination (hidden on mobile) */}
      <nav
        className="hidden md:flex items-center gap-2"
        aria-label="Pagination Navigation"
      >
        {/* Previous button */}
        {currentPage > 1 ? (
          <Button
            as={Link}
            href={createPageURL(currentPage - 1)}
            variant="flat"
            size="sm"
            className="min-w-unit-10"
            startContent={<ChevronLeft className="w-4 h-4" />}
            aria-label={`Go to page ${currentPage - 1}`}
          >
            Previous
          </Button>
        ) : (
          <Button
            variant="flat"
            size="sm"
            isDisabled
            className="min-w-unit-10"
            startContent={<ChevronLeft className="w-4 h-4" />}
            aria-label="Previous page (disabled)"
          >
            Previous
          </Button>
        )}

        {/* First page + ellipsis */}
        {desktopVisiblePages[0] > 1 && (
          <>
            <Button
              as={Link}
              href={createPageURL(1)}
              variant={1 === currentPage ? "solid" : "flat"}
              color={1 === currentPage ? "primary" : "default"}
              size="sm"
              className="min-w-unit-10"
              aria-label="Go to page 1"
              aria-current={1 === currentPage ? "page" : undefined}
            >
              1
            </Button>
            {desktopVisiblePages[0] > 2 && (
              <span className="text-default-500 px-2" aria-hidden="true">
                ...
              </span>
            )}
          </>
        )}

        {/* Visible page numbers */}
        {desktopVisiblePages.map((page: number) => {
          const isCurrentPage = page === currentPage;
          return (
            <Button
              key={page}
              as={Link}
              href={createPageURL(page)}
              variant={isCurrentPage ? "solid" : "flat"}
              color={isCurrentPage ? "primary" : "default"}
              size="sm"
              className={`min-w-unit-10 ${isCurrentPage ? "text-primary-foreground" : "text-ink"}`}
              aria-label={`Go to page ${page}`}
              aria-current={isCurrentPage ? "page" : undefined}
            >
              {page}
            </Button>
          );
        })}

        {/* Last page + ellipsis */}
        {desktopVisiblePages[desktopVisiblePages.length - 1] < totalPages && (
          <>
            {desktopVisiblePages[desktopVisiblePages.length - 1] <
              totalPages - 1 && (
              <span className="text-default-500 px-2" aria-hidden="true">
                ...
              </span>
            )}
            <Button
              as={Link}
              href={createPageURL(totalPages)}
              variant={totalPages === currentPage ? "solid" : "flat"}
              color={totalPages === currentPage ? "primary" : "default"}
              size="sm"
              className="min-w-unit-10"
              aria-label={`Go to page ${totalPages}`}
              aria-current={totalPages === currentPage ? "page" : undefined}
            >
              {totalPages}
            </Button>
          </>
        )}

        {/* Next button */}
        {currentPage < totalPages ? (
          <Button
            as={Link}
            href={createPageURL(currentPage + 1)}
            variant="flat"
            size="sm"
            className="min-w-unit-10"
            endContent={<ChevronRight className="w-4 h-4" />}
            aria-label={`Go to page ${currentPage + 1}`}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="flat"
            size="sm"
            isDisabled
            className="min-w-unit-10"
            endContent={<ChevronRight className="w-4 h-4" />}
            aria-label="Next page (disabled)"
          >
            Next
          </Button>
        )}
      </nav>

      {/* SEO-friendly page links for search engines */}
      <div className="sr-only">
        <h2>Page Navigation</h2>
        <ul>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <li key={page}>
              <Link
                href={createPageURL(page)}
                aria-current={page === currentPage ? "page" : undefined}
              >
                Page {page}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
